import { useRum } from 'common-rum';
import { useCommonTracking } from 'common-tracking';
import { getUserId, validateEmail } from 'common-utils';
import {
  A,
  Box,
  Button,
  Input,
  P,
  rem,
  Spacer,
  Stack,
  styled,
  Text,
  theme,
} from 'design-system';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthContext } from '../auth-context';
import { EnhancedLinkButton } from '../components/enhanced-link-button';
import { SocialLoginButtons } from '../components/social-login-buttons';
import { SocialLoginDialog } from '../components/social-login-dialog';
import { Wiring } from '../pages/auth-page-utility';

type FormInput = {
  email: string;
  password: string;
};

type Props = {
  defaultEmailValue?: string;
  onSignIn?: () => void;
  onSignUp?: (token: string) => void;
  checkFirstTimeUse?: (token: string) => void;
  wiring: {
    forgotPassword: Wiring;
    signUpLink: Wiring;
  };
  editorialDesk?: boolean;
  supportEmail?: string;
  switchScreen?: boolean;
  defaultPF?: boolean;
};

export const LoginForm = (props: Props) => {
  const {
    authState,
    socialSignIn,
    listeners: { onUserCreated },
  } = useAuthContext();
  const { trackCommonEvent } = useCommonTracking();
  const isAlreadyTracked = useRef(true);
  const editorialDesk = props.editorialDesk ?? false;
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    defaultValues: { email: props.defaultEmailValue ?? '' },
  });
  const rum = useRum();
  const { onSignIn, onSignUp } = props;

  useEffect(() => {
    if (isAlreadyTracked.current) {
      isAlreadyTracked.current = false;
      const ssoId = getUserId();
      trackCommonEvent({
        type: 'login-in-intent',
        anonymousOrSignedIn: ssoId ? 'signed_in' : 'anonymous',
      });
    }
  }, [trackCommonEvent]);

  useEffect(() => {
    if (authState.name === 'logged out' && authState.error) {
      setLoading(false);
      trackCommonEvent({
        type: 'login-in-failed',
        emailId: watch('email'),
        message: JSON.stringify(authState.error),
      });
      console.error(
        `login-in-failed: ${btoa(watch('email'))}`,
        JSON.stringify(authState.error),
      );
      try {
        rum.pushError(new Error(`${JSON.stringify(authState.error)}`), {
          type: 'Login Error',
        });
      } catch {}
    }
  }, [authState, trackCommonEvent, watch, rum]);

  const shouldEnableSocialButtons =
    socialSignIn?.enabled === 'true' &&
    socialSignIn?.useGoogleOAuth &&
    socialSignIn?.google?.clientId;

  const shouldEnableCustomRlifeOAuth =
    socialSignIn?.enabled === 'true' && !socialSignIn?.useGoogleOAuth;

  return (
    <>
      {shouldEnableSocialButtons && (
        <SocialLoginButtons
          onSignIn={props.onSignIn}
          checkFirstTimeUse={props.checkFirstTimeUse}
          onSignUp={onSignUp}
        />
      )}
      {shouldEnableCustomRlifeOAuth && (
        <SocialLoginDialog
          onSignIn={props.onSignIn}
          checkFirstTimeUse={props.checkFirstTimeUse}
        />
      )}
      <FullPageForm
        onSubmit={handleSubmit(async (credentials) => {
          return new Promise<void>((resolve) => {
            try {
              setLoading(true);
              authState.logIn(credentials, async (userId, token) => {
                if (props.checkFirstTimeUse) {
                  await props.checkFirstTimeUse?.(token);
                }
                trackCommonEvent({ type: 'login-in', userId: userId });
                onUserCreated(token);
                await onSignIn?.();
                setLoading(false);
                resolve();
              });
            } catch {
              setLoading(false);
            }
          });
        })}
      >
        <Input
          autoFocus={true}
          label={
            <span>
              Email <span style={{ color: theme.colors.red11.value }}>*</span>
            </span>
          }
          // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
          type="email"
          placeholder="isaac.newton@university.com"
          {...register('email', {
            onChange: () => {
              const value = getValues('email');
              const normalizedValue = value.toLowerCase().trim();
              setValue('email', normalizedValue);
            },
            required: { value: true, message: 'E-mail is required' },
            validate: validateEmail,
            onBlur: (event) => {
              const value = event.target.value;
              const normalizedValue = value.toLowerCase().trim();
              setValue('email', normalizedValue);
            },
          })}
          error={errors.email?.message}
          readOnly={props.defaultPF ? true : false}
          disabled={props.defaultPF}
        />
        <Input
          // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
          label={
            <span>
              Password{' '}
              <span style={{ color: theme.colors.red11.value }}>*</span>
            </span>
          }
          type="password"
          {...register('password', {
            required: { value: true, message: 'Password is required' },
          })}
          error={errors.password?.message}
        />
        <ErrorBox
          css={{
            display:
              authState.name === 'logged out' && authState.error
                ? 'block'
                : 'none',
          }}
        >
          {authState.name === 'logged out' ? (
            <Text>
              {typeof authState.error === 'string' ? (
                authState.error
              ) : (
                <>
                  Something went wrong, please try again later. If the error
                  continues please contact support{' '}
                  <a
                    href={`mailto:${
                      props.supportEmail || 'hello@paperpal.com'
                    }`}
                  >
                    here
                  </a>
                  .
                </>
              )}
            </Text>
          ) : (
            <Text />
          )}
        </ErrorBox>
        <FooterDiv>
          <ButtonContainer>
            <Spacer height={6} />
            <Button
              fullWidth
              loading={
                loading ||
                authState.name === 'checking if logged in' ||
                authState.name === 'logging in'
              }
              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
              type="submit"
              id="login-button"
              data-agl-cvt={7}
            >
              {editorialDesk ? 'Sign in' : 'Log in'}
            </Button>
          </ButtonContainer>
        </FooterDiv>
        <Spacer height={16} />
        <Stack direction="column" spacing={8} css={{ textAlign: 'center' }}>
          {editorialDesk || props.switchScreen ? (
            <></>
          ) : (
            <P>
              Don’t have an account?{' '}
              <EnhancedLinkButton
                emailValue={watch('email')}
                // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
                wiring={props.wiring.signUpLink}
              >
                Sign up
              </EnhancedLinkButton>
            </P>
          )}
          <P>
            Forgot password?{' '}
            <EnhancedLinkButton
              emailValue={watch('email')}
              // tabIndex={0} Due to accessibility issues, we are not using tabIndex here
              wiring={props.wiring.forgotPassword}
            >
              Get a one-time password
            </EnhancedLinkButton>
          </P>
          {editorialDesk ? (
            <>
              <P style={{ fontSize: '12px' }}>
                If you face any difficulty, please write to us at{' '}
                <A
                  href={`mailto:${props.supportEmail || 'hello@paperpal.com'}`}
                >
                  {props.supportEmail || 'hello@paperpal.com'}.
                </A>
              </P>
            </>
          ) : (
            <></>
          )}
        </Stack>
      </FullPageForm>
    </>
  );
};

const FooterDiv = styled('div', {
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
});

const FullPageForm = styled('form', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});
FullPageForm.displayName = 'FullPageForm';

const ButtonGroup = styled(Box, {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: rem(12),
});

const ErrorBox = styled(Box, {
  border: `1px solid ${theme.colors.red10}`,
  padding: rem(10),
  borderRadius: rem(8),
  color: theme.colors.red10,
});

const ButtonContainer = styled(ButtonGroup, {
  flex: 1,
});
